import { clsx, type ClassValue } from "clsx";
import _deepEqual from "fast-deep-equal";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Re-export fast-deep-equal for use across the app. */
export const deepEqual: (a: unknown, b: unknown) => boolean = _deepEqual;

/** Format-agnostic color with alpha via CSS color-mix. Works with hex, rgb, hsl, named colors, etc. */
export function colorAlpha(color: string, percent: number): string {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

/** Invert a hex color's lightness for contrast. Dark mode pushes lighter, light mode pushes darker. */
export function colorInverse(hex: string, dark: boolean): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  let h = 0;
  let s = 0;
  if (d > 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d + 6) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }

  const invH = (h + 180) % 360;
  const invL = dark ? 0.5 + (1 - l) * 0.5 : 0.25 + (1 - l) * 0.5;

  // HSL → RGB → hex
  const c2 = (1 - Math.abs(2 * invL - 1)) * s;
  const x = c2 * (1 - Math.abs(((invH / 60) % 2) - 1));
  const m = invL - c2 / 2;
  let r1: number, g1: number, b1: number;
  if (invH < 60) [r1, g1, b1] = [c2, x, 0];
  else if (invH < 120) [r1, g1, b1] = [x, c2, 0];
  else if (invH < 180) [r1, g1, b1] = [0, c2, x];
  else if (invH < 240) [r1, g1, b1] = [0, x, c2];
  else if (invH < 300) [r1, g1, b1] = [x, 0, c2];
  else [r1, g1, b1] = [c2, 0, x];

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
}
